import React from 'react';
import { App, Form, InputNumber, Slider, Switch } from 'antd';

import CacheExpirationSlider from './components/CacheExpirationSlider';
import ButtonWithRole from '../../components/ButtonWithRole';
import Role from '~features/PrerenderUser/Role';
import { expirationIndexToReadable } from './utils/expirationIndexToReadable';

const DEFAULT_SLOW_RENDER_TRIGGER = 10; // 10 seconds
export const MIN_DATA_RETENTION_DAYS = 7;
export const MAX_DATA_RETENTION_DAYS = 730;

function expirationHoursToIndex(hours: number) {
  if (hours < 0) return null;
  if (hours > 24) return hours / 24 + 23;
  return hours;
}

function expirationIndexToHours(index: number | null) {
  if (!index) return -1;
  if (index > 24) return (index - 23) * 24;
  return index;
}

const NO_PLAN_WINDOW = Number.POSITIVE_INFINITY;

function isShorteningRetention(
  currentDays: number | null,
  nextDays: number | null,
  planRetentionDays: number | undefined
): boolean {
  const planWindow = planRetentionDays ?? NO_PLAN_WINDOW;
  return (nextDays ?? planWindow) < (currentDays ?? planWindow);
}

function confirmShorteningRetention(modal: ReturnType<typeof App.useApp>['modal'], days: number, onOk: () => void) {
  modal.confirm({
    title: 'Shorten the data retention window?',
    content: `Pages that have not been served or refreshed in the last ${days} days will be removed from the cache and cannot be recovered. The removal runs in nightly batches, so a large reduction completes over several days rather than at once.`,
    okText: 'Shorten window',
    okButtonProps: { danger: true },
    onOk,
  });
}

function retentionHelp(planRetentionDays: number | undefined): string {
  const fallback =
    planRetentionDays === undefined
      ? 'Your plan has no retention limit, so leave empty to keep pages indefinitely.'
      : `Leave empty to use your plan's ${planRetentionDays}-day window.`;
  return `Pages not served or refreshed within this window are removed from the cache. ${fallback}`;
}

export type DomainCacheSettings = {
  slowRenderTrigger: number;
  cacheExpirationHours: number;
  dataRetentionDays: number | null;
};

type FormValues = {
  slowRenderTrigger: number;
  cacheExpirationHours: number | null;
  automaticRecache: boolean;
  dataRetentionDays?: number | null;
};

type DomainCustomCacheExpirationProps = {
  isLoading?: boolean;
  disabled?: boolean;
  slowRenderTrigger: number;
  cacheExpirationHours: number;
  dataRetentionDays: number | null;
  planRetentionDays?: number;
  automaticRecache?: boolean;
  cacheExpirationDaysInterval: [number, number];
  onSave: (values: DomainCacheSettings) => void;
};

function DomainCustomCacheExpiration({
  isLoading,
  disabled,
  onSave,
  slowRenderTrigger,
  cacheExpirationHours,
  dataRetentionDays,
  planRetentionDays,
  automaticRecache = true,
  cacheExpirationDaysInterval,
}: DomainCustomCacheExpirationProps) {
  const { modal } = App.useApp();
  const [slowRenderTriggerLabel, setSlowRenderTriggerLabel] = React.useState(`${DEFAULT_SLOW_RENDER_TRIGGER} seconds`);

  const [automaticCacheEnabled, setAutomaticCacheEnabled] = React.useState(cacheExpirationHours > 0);
  const [form] = Form.useForm<FormValues>();
  const cacheExpirationIndex = Form.useWatch('cacheExpirationHours', form);

  const onSlowRenderTriggerChange = (value: number) => setSlowRenderTriggerLabel(`${value} seconds`);

  const onAutomaticRecacheChange = (enabled: boolean) => {
    const cacheExpirationIndex = enabled ? expirationHoursToIndex(cacheExpirationHours) : null;
    form.setFieldsValue({ cacheExpirationHours: cacheExpirationIndex });
    setAutomaticCacheEnabled(enabled);
  };

  const onSubmit = (values: FormValues) => {
    const settings: DomainCacheSettings = {
      slowRenderTrigger: values.slowRenderTrigger,
      cacheExpirationHours: expirationIndexToHours(values.cacheExpirationHours),
      dataRetentionDays: values.dataRetentionDays ?? null,
    };

    const retentionDaysNext = settings.dataRetentionDays ?? planRetentionDays;
    if (
      retentionDaysNext !== undefined &&
      isShorteningRetention(dataRetentionDays, settings.dataRetentionDays, planRetentionDays)
    ) {
      confirmShorteningRetention(modal, retentionDaysNext, () => onSave(settings));
      return;
    }

    onSave(settings);
  };

  const cacheExpirationLabel = `Cache Expiration (${expirationIndexToReadable(cacheExpirationIndex)})`;

  React.useEffect(() => {
    const cacheExpirationIndex = expirationHoursToIndex(cacheExpirationHours);
    form.setFieldsValue({
      slowRenderTrigger,
      cacheExpirationHours: cacheExpirationIndex,
      automaticRecache: cacheExpirationHours > 0,
      dataRetentionDays,
    });

    onSlowRenderTriggerChange(slowRenderTrigger);
  }, [slowRenderTrigger, cacheExpirationHours, automaticRecache, dataRetentionDays]);

  return (
    <Form layout="vertical" onFinish={onSubmit} form={form} disabled={disabled}>
      <Form.Item name="slowRenderTrigger" label={`Slow Render Trigger (${slowRenderTriggerLabel})`}>
        <Slider min={10} max={70} marks={{ 10: '10s', 70: '70s' }} onChangeComplete={onSlowRenderTriggerChange} />
      </Form.Item>
      <CacheExpirationSlider
        daysInterval={cacheExpirationDaysInterval}
        value={cacheExpirationHours}
        disabled={!automaticCacheEnabled}
        label={cacheExpirationLabel}
      />
      <Form.Item name="dataRetentionDays" label="Data Retention (days)" extra={retentionHelp(planRetentionDays)}>
        <InputNumber
          min={MIN_DATA_RETENTION_DAYS}
          max={MAX_DATA_RETENTION_DAYS}
          precision={0}
          placeholder={planRetentionDays === undefined ? 'No limit' : `${planRetentionDays}`}
          style={{ width: '100%' }}
        />
      </Form.Item>
      <Form.Item name="automaticRecache" label="Automatic Recache" layout="horizontal">
        <Switch onChange={onAutomaticRecacheChange} />
      </Form.Item>
      <Form.Item style={{ marginBottom: 0 }}>
        <ButtonWithRole
          disabledFor={[Role.BillingManager, Role.Guest]}
          type="primary"
          htmlType="submit"
          loading={isLoading}
        >
          Update
        </ButtonWithRole>
      </Form.Item>
    </Form>
  );
}

export default DomainCustomCacheExpiration;
